/**
 * FL-67: 住所データ抽出・正規化ユーティリティ
 *
 * OCRで抽出されたテキストから住所情報を抽出・整形する
 * Phase 1: 基本的なパターンマッチング
 * Phase 2: より高度な住所解析とAPI検証
 */

import { ExtractedAddress, AddressExtractionOptions, AddressValidationResult } from '../types';
import logger from './logger';

/**
 * FL-67: 電話番号抽出結果
 */
export interface ExtractedPhoneNumber {
  id: string;
  rawText: string;
  phoneNumber: string;
  confidence: number;
  extractedAt: string;
  isVerified: boolean;
}

/**
 * 日本の都道府県一覧
 */
const PREFECTURES = [
  '北海道',
  '青森県',
  '岩手県',
  '宮城県',
  '秋田県',
  '山形県',
  '福島県',
  '茨城県',
  '栃木県',
  '群馬県',
  '埼玉県',
  '千葉県',
  '東京都',
  '神奈川県',
  '新潟県',
  '富山県',
  '石川県',
  '福井県',
  '山梨県',
  '長野県',
  '岐阜県',
  '静岡県',
  '愛知県',
  '三重県',
  '滋賀県',
  '京都府',
  '大阪府',
  '兵庫県',
  '奈良県',
  '和歌山県',
  '鳥取県',
  '島根県',
  '岡山県',
  '広島県',
  '山口県',
  '徳島県',
  '香川県',
  '愛媛県',
  '高知県',
  '福岡県',
  '佐賀県',
  '長崎県',
  '熊本県',
  '大分県',
  '宮崎県',
  '鹿児島県',
  '沖縄県',
];

/**
 * 住所抽出の正規表現パターン
 */
const ADDRESS_PATTERNS = {
  // 郵便番号: 〒123-4567 または 123-4567
  postalCode: /(?:〒\s*)?(\d{3}[-‐－]\d{4})/g,

  // 都道府県
  prefecture: new RegExp(`(${PREFECTURES.join('|')})`, 'g'),

  // 市区町村（基本パターン）
  city: /([^\s]{1,10}(?:市|区|町|村))/g,

  // 町名・丁目
  town: /([^\s]{1,20}(?:町|丁目|番地|番|号|chome|banchi|ban|go))/g,

  // 建物名
  building: /([^\s]{1,30}(?:ビル|マンション|アパート|ハイツ|コーポ|レジデンス|タワー|プラザ|館))/g,

  // 部屋番号
  room: /(\d{1,4}(?:号室?|号|室))/g,
};

/**
 * テキストの正規化
 */
export const normalizeText = (text: string): string => {
  return (
    text
      // 全角数字を半角に変換
      .replace(/[０-９]/g, s => String.fromCharCode(s.charCodeAt(0) - 0xfee0))
      // 全角英字を半角に変換
      .replace(/[Ａ-Ｚａ-ｚ]/g, s => String.fromCharCode(s.charCodeAt(0) - 0xfee0))
      // 各種ハイフンを統一
      .replace(/[‐－−]/g, '-')
      // 余分な空白を削除
      .replace(/\s+/g, ' ')
      .trim()
  );
};

/**
 * 郵便番号を抽出
 */
const extractPostalCode = (text: string): string | undefined => {
  const matches = text.match(ADDRESS_PATTERNS.postalCode);
  if (matches && matches.length > 0) {
    // 最初に見つかった郵便番号を返す
    return matches[0].replace(/[〒\s]/g, '');
  }
  return undefined;
};

/**
 * 都道府県を抽出
 */
const extractPrefecture = (text: string): string | undefined => {
  // まず通常のパターンでマッチを試す
  const matches = text.match(ADDRESS_PATTERNS.prefecture);
  if (matches && matches.length > 0) {
    return matches[0];
  }

  // スペースが入った都道府県名もチェック（"東京 都" → "東京都"、"北海 道" → "北海道"）
  const spacedPrefecturePattern = /([^\s]{1,10})\s+(都|府|県|道)/g;
  const spacedMatches = text.match(spacedPrefecturePattern);
  if (spacedMatches && spacedMatches.length > 0) {
    const spacedPref = spacedMatches[0].replace(/\s+/g, ''); // スペースを除去
    // 正規の都道府県名リストにあるかチェック
    if (PREFECTURES.includes(spacedPref)) {
      logger.info(
        `[addressExtraction] スペース付き都道府県を検出: "${spacedMatches[0]}" → "${spacedPref}"`
      );
      return spacedPref;
    }
  }

  return undefined;
};

/**
 * 市区町村を抽出
 */
const extractCity = (text: string, prefecture?: string): string | undefined => {
  let searchText = text;

  // 都道府県が分かっている場合は、その後から検索
  if (prefecture) {
    const prefIndex = text.indexOf(prefecture);
    if (prefIndex !== -1) {
      searchText = text.substring(prefIndex + prefecture.length);
    }
  }

  // 通常パターンでマッチを試す
  const matches = searchText.match(ADDRESS_PATTERNS.city);
  if (matches && matches.length > 0) {
    return matches[0];
  }

  // スペースが入った市区町村名もチェック（"渋谷 区" → "渋谷区"）
  const spacedCityPattern = /([^\s]{1,10})\s+(市|区|町|村)/g;
  const spacedMatches = searchText.match(spacedCityPattern);
  if (spacedMatches && spacedMatches.length > 0) {
    const spacedCity = spacedMatches[0].replace(/\s+/g, ''); // スペースを除去
    logger.info(
      `[addressExtraction] スペース付き市区町村を検出: "${spacedMatches[0]}" → "${spacedCity}"`
    );
    return spacedCity;
  }

  return undefined;
};

/**
 * 町名・番地を抽出
 */
const extractTown = (text: string, city?: string): string | undefined => {
  let searchText = text;

  // 市区町村が分かっている場合は、その後から検索
  if (city) {
    const cityIndex = text.indexOf(city);
    if (cityIndex !== -1) {
      searchText = text.substring(cityIndex + city.length);
    }
  }

  // 通常パターンでマッチを試す
  const matches = searchText.match(ADDRESS_PATTERNS.town);
  if (matches && matches.length > 0) {
    // 複数の町名・番地がある場合は結合
    return matches.slice(0, 3).join(''); // 最大3つまで
  }

  // スペースが入った町名・番地もチェック（"神宮 前 1-1-1" → "神宮前1-1-1"）
  const spacedTownPattern = /([^\s]{1,20}(?:\s+[^\s]{1,20})*)\s*(\d+[-‐－]\d+[-‐－]\d+)/g;
  let spacedMatch;
  const spacedMatches = [];
  while ((spacedMatch = spacedTownPattern.exec(searchText)) !== null) {
    const spacedTown = (spacedMatch[1] + spacedMatch[2]).replace(/\s+/g, ''); // スペースを除去
    spacedMatches.push(spacedTown);
  }

  if (spacedMatches.length > 0) {
    const result = spacedMatches.slice(0, 3).join(''); // 最大3つまで
    logger.info(`[addressExtraction] スペース付き町名・番地を検出: "${result}"`);
    return result;
  }

  return undefined;
};

/**
 * 建物名を抽出
 */
const extractBuilding = (text: string): string | undefined => {
  // 通常パターンでマッチを試す
  const matches = text.match(ADDRESS_PATTERNS.building);
  if (matches && matches.length > 0) {
    return matches[0];
  }

  // スペースが入った建物名もチェック（"〇 O 〇 ビル" → "〇O〇ビル"）
  const spacedBuildingPattern =
    /([^\s]{1,30}(?:\s+[^\s]{1,30})*)\s*(ビル|マンション|アパート|ハイツ|コーポ|レジデンス|タワー|プラザ|館)/g;
  const spacedMatches = text.match(spacedBuildingPattern);
  if (spacedMatches && spacedMatches.length > 0) {
    const spacedBuilding = spacedMatches[0].replace(/\s+/g, ''); // スペースを除去
    logger.info(
      `[addressExtraction] スペース付き建物名を検出: "${spacedMatches[0]}" → "${spacedBuilding}"`
    );
    return spacedBuilding;
  }

  return undefined;
};

/**
 * 部屋番号を抽出
 */
const extractRoom = (text: string): string | undefined => {
  const matches = text.match(ADDRESS_PATTERNS.room);
  if (matches && matches.length > 0) {
    return matches[0];
  }
  return undefined;
};

/**
 * 完全な住所文字列を構築
 */
const buildFullAddress = (components: {
  postalCode?: string;
  prefecture?: string;
  city?: string;
  town?: string;
  building?: string;
  room?: string;
}): string => {
  const parts: string[] = [];

  if (components.prefecture) parts.push(components.prefecture);
  if (components.city) parts.push(components.city);
  if (components.town) parts.push(components.town);
  if (components.building) parts.push(components.building);
  if (components.room) parts.push(components.room);

  return parts.join('');
};

/**
 * 住所の信頼度を計算
 */
const calculateAddressConfidence = (components: {
  postalCode?: string;
  prefecture?: string;
  city?: string;
  town?: string;
  building?: string;
  room?: string;
}): number => {
  let score = 0;
  let maxScore = 0;

  // 郵便番号: 20点
  maxScore += 20;
  if (components.postalCode) score += 20;

  // 都道府県: 25点
  maxScore += 25;
  if (components.prefecture) score += 25;

  // 市区町村: 25点
  maxScore += 25;
  if (components.city) score += 25;

  // 町名・番地: 20点
  maxScore += 20;
  if (components.town) score += 20;

  // 建物名: 5点
  maxScore += 5;
  if (components.building) score += 5;

  // 部屋番号: 5点
  maxScore += 5;
  if (components.room) score += 5;

  return maxScore > 0 ? score / maxScore : 0;
};

/**
 * OCRテキストから住所を抽出
 */
export const extractAddressFromText = (
  rawText: string,
  options: AddressExtractionOptions
): ExtractedAddress[] => {
  logger.info(`[addressExtraction] 住所抽出開始 - 元テキスト: "${rawText}"`);
  const normalizedText = normalizeText(rawText);
  logger.info(`[addressExtraction] 正規化後: "${normalizedText}"`);
  const addresses: ExtractedAddress[] = [];

  // 行ごとに分割して処理
  const lines = normalizedText.split('\n').filter(line => line.trim().length > 0);

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // 住所らしいパターンを含む行かチェック
    const hasAddressPattern =
      ADDRESS_PATTERNS.postalCode.test(line) ||
      ADDRESS_PATTERNS.prefecture.test(line) ||
      ADDRESS_PATTERNS.city.test(line);

    if (!hasAddressPattern) continue;

    // 複数行にまたがる住所の場合を考慮（次の行も含める）
    let extendedLine = line;
    if (i < lines.length - 1) {
      extendedLine += ' ' + lines[i + 1];
    }

    // 住所コンポーネントを抽出
    logger.info(`[addressExtraction] 処理対象行: "${extendedLine}"`);
    const postalCode = options.includePostalCode ? extractPostalCode(extendedLine) : undefined;
    const prefecture = extractPrefecture(extendedLine);
    const city = extractCity(extendedLine, prefecture);
    const town = extractTown(extendedLine, city);
    const buildingName = options.includeBuilding ? extractBuilding(extendedLine) : undefined;
    const roomNumber = options.includeBuilding ? extractRoom(extendedLine) : undefined;

    logger.info(`[addressExtraction] 抽出結果:`, {
      postalCode,
      prefecture,
      city,
      town,
      buildingName,
      roomNumber,
    });

    // 最低限の住所情報があるかチェック
    if (!prefecture && !city && !town) continue;

    const components = {
      postalCode,
      prefecture,
      city,
      town,
      buildingName,
      roomNumber,
    };

    const fullAddress = buildFullAddress(components);
    const confidence = calculateAddressConfidence(components);

    // 信頼度が閾値を下回る場合はスキップ
    if (confidence < options.confidenceThreshold) continue;

    const extractedAddress: ExtractedAddress = {
      id: `addr-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      rawText: line,
      postalCode,
      prefecture,
      city,
      town,
      streetAddress: town,
      buildingName,
      roomNumber,
      fullAddress,
      confidence,
      extractedAt: new Date().toISOString(),
      isVerified: false,
    };

    addresses.push(extractedAddress);
  }

  // 重複する住所を除去
  const finalAddresses = deduplicateAddresses(addresses);
  logger.info(`[addressExtraction] 住所抽出完了: ${finalAddresses.length}件`, finalAddresses);
  return finalAddresses;
};

/**
 * 重複する住所を除去
 */
const deduplicateAddresses = (addresses: ExtractedAddress[]): ExtractedAddress[] => {
  const seen = new Set<string>();
  return addresses.filter(addr => {
    const key = addr.fullAddress.toLowerCase().replace(/\s/g, '');
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

/**
 * 住所の基本検証（パターンベース）
 */
export const validateAddressBasic = (address: ExtractedAddress): AddressValidationResult => {
  let isValid = true;
  let confidence = address.confidence;
  const errors: string[] = [];

  // 郵便番号の形式チェック
  if (address.postalCode && !/^\d{3}-\d{4}$/.test(address.postalCode)) {
    errors.push('郵便番号の形式が正しくありません');
    confidence -= 0.2;
  }

  // 都道府県の妥当性チェック
  if (address.prefecture && !PREFECTURES.includes(address.prefecture)) {
    errors.push('都道府県名が認識されません');
    confidence -= 0.3;
  }

  // 最低限の住所情報があるかチェック
  if (!address.prefecture && !address.city) {
    errors.push('都道府県または市区町村の情報が必要です');
    isValid = false;
  }

  if (errors.length > 0) {
    isValid = false;
  }

  return {
    isValid,
    confidence: Math.max(0, Math.min(1, confidence)),
    errorMessage: errors.length > 0 ? errors.join(', ') : undefined,
  };
};

/**
 * 複数のOCR結果から住所を抽出してマージ
 */
export const extractAddressesFromMultipleTexts = (
  texts: string[],
  options: AddressExtractionOptions
): ExtractedAddress[] => {
  const allAddresses: ExtractedAddress[] = [];

  texts.forEach(text => {
    const addresses = extractAddressFromText(text, options);
    allAddresses.push(...addresses);
  });

  // 重複除去と信頼度でソート
  return deduplicateAddresses(allAddresses).sort((a, b) => b.confidence - a.confidence);
};

/**
 * デフォルトの住所抽出オプション
 */
export const DEFAULT_ADDRESS_EXTRACTION_OPTIONS: AddressExtractionOptions = {
  includePostalCode: true,
  includeBuilding: true,
  normalizeFormat: true,
  validateWithAPI: false,
  confidenceThreshold: 0.3, // Phase 1では低めに設定
};

/**
 * 住所情報のフォーマット（表示用）
 */
export const formatAddressForDisplay = (address: ExtractedAddress): string => {
  const parts: string[] = [];

  if (address.postalCode) {
    parts.push(`〒${address.postalCode}`);
  }

  parts.push(address.fullAddress);

  return parts.join(' ');
};

/**
 * 住所情報をフォームフィールド用にマッピング
 */
export const mapAddressToFormFields = (address: ExtractedAddress) => {
  return {
    postalCode: address.postalCode || '',
    prefecture: address.prefecture || '',
    city: address.city || '',
    streetAddress: address.streetAddress || '',
    buildingName: address.buildingName || '',
    fullAddress: address.fullAddress || '',
  };
};

/**
 * FL-67: 電話番号抽出パターン
 */
const PHONE_PATTERNS = [
  // 接頭辞付きハイフン区切り（TEL: 03-1234-5678、電話: 090-1234-5678等）
  /(?:TEL|電話|Phone|Tel|phone|tel)[\s：:]*(\d{2,4})-(\d{2,4})-(\d{4})/gi,
  // ハイフン区切り（03-1234-5678、090-1234-5678等）
  /(\d{2,4})-(\d{2,4})-(\d{4})/g,
  // 接頭辞付き括弧（TEL: (03)1234-5678等）
  /(?:TEL|電話|Phone|Tel|phone|tel)[\s：:]*\((\d{2,4})\)(\d{2,4})-(\d{4})/gi,
  // 括弧付き（(03)1234-5678、(090)1234-5678等）
  /\((\d{2,4})\)(\d{2,4})-(\d{4})/g,
  // 接頭辞付きスペース区切り（TEL: 03 1234 5678等）
  /(?:TEL|電話|Phone|Tel|phone|tel)[\s：:]+(\d{2,4})\s+(\d{2,4})\s+(\d{4})/gi,
  // スペース区切り（03 1234 5678等）
  /(\d{2,4})\s+(\d{2,4})\s+(\d{4})/g,
  // 接頭辞付き連続数字（TEL: 0312345678等）
  /(?:TEL|電話|Phone|Tel|phone|tel)[\s：:]+(\d{10,11})/gi,
  // 連続数字（0312345678、09012345678等）
  /(\d{10,11})/g,
];

/**
 * FL-67: テキストから電話番号を抽出
 */
export const extractPhoneNumberFromText = (text: string): ExtractedPhoneNumber[] => {
  const phoneNumbers: ExtractedPhoneNumber[] = [];
  const now = new Date().toISOString();

  logger.info(`[addressExtraction] 電話番号抽出開始 - テキスト: "${text}"`);

  try {
    PHONE_PATTERNS.forEach((pattern, patternIndex) => {
      logger.info(`[addressExtraction] パターン ${patternIndex} をテスト: ${pattern}`);

      // パターンのlastIndexをリセット（globalフラグ対応）
      pattern.lastIndex = 0;

      let match;
      while ((match = pattern.exec(text)) !== null) {
        logger.info(`[addressExtraction] パターン ${patternIndex} でマッチ:`, match);
        const rawMatch = match[0];
        let phoneNumber = '';
        let confidence = 0.7; // 基本信頼度

        // パターンごとの処理
        if (pattern === PHONE_PATTERNS[0]) {
          // 接頭辞付きハイフン区切り: TEL: 03-1234-5678
          phoneNumber = `${match[1]}-${match[2]}-${match[3]}`;
          confidence = 0.95; // 接頭辞があるので信頼度が高い
        } else if (pattern === PHONE_PATTERNS[1]) {
          // ハイフン区切り: 03-1234-5678
          phoneNumber = rawMatch;
          confidence = 0.9;
        } else if (pattern === PHONE_PATTERNS[2]) {
          // 接頭辞付き括弧: TEL: (03)1234-5678 → 03-1234-5678
          phoneNumber = `${match[1]}-${match[2]}-${match[3]}`;
          confidence = 0.9;
        } else if (pattern === PHONE_PATTERNS[3]) {
          // 括弧付き: (03)1234-5678 → 03-1234-5678
          phoneNumber = `${match[1]}-${match[2]}-${match[3]}`;
          confidence = 0.8;
        } else if (pattern === PHONE_PATTERNS[4]) {
          // 接頭辞付きスペース区切り: TEL: 03 1234 5678 → 03-1234-5678
          phoneNumber = `${match[1]}-${match[2]}-${match[3]}`;
          confidence = 0.85;
        } else if (pattern === PHONE_PATTERNS[5]) {
          // スペース区切り: 03 1234 5678 → 03-1234-5678
          phoneNumber = `${match[1]}-${match[2]}-${match[3]}`;
          confidence = 0.7;
        } else if (pattern === PHONE_PATTERNS[6]) {
          // 接頭辞付き連続数字: TEL: 0312345678 → 03-1234-5678
          const digits = match[1];
          if (digits.length === 10) {
            phoneNumber = `${digits.slice(0, 2)}-${digits.slice(2, 6)}-${digits.slice(6)}`;
          } else if (digits.length === 11) {
            if (digits.startsWith('090') || digits.startsWith('080') || digits.startsWith('070')) {
              phoneNumber = `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
            } else {
              phoneNumber = `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
            }
          }
          confidence = 0.8; // 接頭辞があるので通常の連続数字より信頼度高め
        } else if (pattern === PHONE_PATTERNS[7]) {
          // 連続数字: 0312345678 → 03-1234-5678
          const digits = match[1];
          if (digits.length === 10) {
            // 市外局番2桁の場合: 0312345678 → 03-1234-5678
            phoneNumber = `${digits.slice(0, 2)}-${digits.slice(2, 6)}-${digits.slice(6)}`;
          } else if (digits.length === 11) {
            // 携帯電話または市外局番3桁: 09012345678 → 090-1234-5678
            if (digits.startsWith('090') || digits.startsWith('080') || digits.startsWith('070')) {
              phoneNumber = `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
            } else {
              phoneNumber = `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
            }
          }
          confidence = 0.6; // 連続数字は信頼度やや低め
        }

        // 有効な電話番号かチェック
        if (phoneNumber && isValidPhoneNumber(phoneNumber)) {
          phoneNumbers.push({
            id: `phone-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            rawText: rawMatch,
            phoneNumber,
            confidence,
            extractedAt: now,
            isVerified: false,
          });
        }

        // 無限ループ防止
        if (!pattern.global) break;
      }
    });

    // 重複除去（同じ電話番号は信頼度の高いものを残す）
    const uniquePhones = new Map<string, ExtractedPhoneNumber>();
    phoneNumbers.forEach(phone => {
      const existing = uniquePhones.get(phone.phoneNumber);
      if (!existing || existing.confidence < phone.confidence) {
        uniquePhones.set(phone.phoneNumber, phone);
      }
    });

    const result = Array.from(uniquePhones.values()).sort((a, b) => b.confidence - a.confidence);
    logger.info(`[addressExtraction] 電話番号抽出完了 - 結果: ${result.length}件`, result);
    return result;
  } catch (error) {
    logger.error('[addressExtraction] 電話番号抽出エラー:', error);
    return [];
  }
};

/**
 * FL-67: 電話番号の有効性チェック
 */
const isValidPhoneNumber = (phoneNumber: string): boolean => {
  // ハイフンを除去して数字のみにする
  const digits = phoneNumber.replace(/-/g, '');

  // 桁数チェック（10桁または11桁）
  if (digits.length !== 10 && digits.length !== 11) {
    return false;
  }

  // 先頭が0で始まるかチェック
  if (!digits.startsWith('0')) {
    return false;
  }

  // 携帯電話番号パターン
  if (
    digits.length === 11 &&
    (digits.startsWith('090') || digits.startsWith('080') || digits.startsWith('070'))
  ) {
    return true;
  }

  // 固定電話番号パターン（10桁）
  if (digits.length === 10) {
    return true;
  }

  // その他の11桁固定電話（市外局番が3桁の場合）
  if (digits.length === 11) {
    return true;
  }

  return false;
};

/**
 * FL-67: 複数のOCR結果から電話番号を抽出してマージ
 */
export const extractPhoneNumbersFromMultipleTexts = (texts: string[]): ExtractedPhoneNumber[] => {
  const allPhoneNumbers: ExtractedPhoneNumber[] = [];

  texts.forEach(text => {
    const phoneNumbers = extractPhoneNumberFromText(text);
    allPhoneNumbers.push(...phoneNumbers);
  });

  // 重複除去と信頼度でソート
  const uniquePhones = new Map<string, ExtractedPhoneNumber>();
  allPhoneNumbers.forEach(phone => {
    const existing = uniquePhones.get(phone.phoneNumber);
    if (!existing || existing.confidence < phone.confidence) {
      uniquePhones.set(phone.phoneNumber, phone);
    }
  });

  return Array.from(uniquePhones.values()).sort((a, b) => b.confidence - a.confidence);
};
